import { defineStore } from 'pinia'
import { FamilyGroupInvite, InvitationResult, ItemPermissions } from '@webapp/gp/GroupAdminModel'
import { useGroupAdminApi } from './GroupAdminApi'
import { AsyncData, LoadMode } from '@webapp/util/AsyncData'
import { AsyncDataStore, VersionedKeyList } from '@webapp/util/AsyncDataStore'
import { useUserStore } from './UserStore'
import { useFamilyGroupStore } from './FamilyGroupStore'
import { useFamilyGroupMemberStore } from './FamilyGroupMemberStore'
import { useTokenManager } from '@webapp/auth/TokenManager'
import { DateTime } from 'luxon'
import { DateTimeUtil } from '@webapp/util/LuxonUtil'
import { assignExisting, isDefined } from '@webapp/util/TypeScriptUtil'
import { useDataGroupStore } from './DataGroupStore'
import { AccessTokenResponse } from '@webapp/auth/AccessTokenResponse'
import { usePermissions } from './Permissions'
import { addRedactRule } from '@/util/RedactRules'

addRedactRule({
    pattern: new RegExp('^/familygroupinvites/[^/]+$'),
    params: ['code']
})

export const useFamilyGroupInviteStore = defineStore('familyGroupInvites', () => {
    const asyncInvites = new Map<string, AsyncData<FamilyGroupInvite>>()
    const asyncPermissions = new Map<string, AsyncData<ItemPermissions>>()

    const gp = useGroupAdminApi()
    const familyGroupStore = useFamilyGroupStore()
    const userStore = useUserStore()
    const dataGroupStore = useDataGroupStore()
    const tokenManager = useTokenManager()
    const { getAsyncPermissionsCore } = usePermissions()

    function getAsyncInvite(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        return id ? getAsyncInviteList([id], loadMode)[0] : undefined
    }
    
    function getAsyncInviteList(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const store = useFamilyGroupInviteStore() // use to get/return reactive store objects
        const loadAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const plainItems = await gp.getPlainByIdsAsync("familygroupinvites", idsToLoad, familyGroupInviteExpand)
            console.log(`Loaded ${plainItems.length} family group invites by id: ${plainItems.map(p => p.id).join(", ")}`)
            return processExpandedFamilyGroupInvites(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncInvites, ids, "FamilyGroupInvite", loadMode, loadAsync)
    }
    
    async function getInviteListAsync(ids: (string | undefined)[]) {
        return (await Promise.all(getAsyncInviteList(ids, LoadMode.EnsureLoaded).map(a => a.whenLoadCompleted))).filter(isDefined)
    }

    function getLoadedInvites(ids: (string | undefined)[]) {
        const store = useFamilyGroupInviteStore() // use to get/return reactive store objects
        return ids.filter(isDefined).map(id => store.asyncInvites.get(id)?.data).filter(isDefined)
    }

    function getAsyncKeyListsForFamilyGroups(familyGroupIds: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        return getAsyncKeyListsForFamilyGroupInvites(familyGroupIds, "FamilyGroup", familyGroupStore.asyncInviteKeys, loadMode, 
            id => `familygroups/${id}/invites`, familyGroupInviteExpand, fm => fm.familyGroupId!)
    }

    function getInvitesForFamilyGroups(familyGroupIds: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const keys = getAsyncKeyListsForFamilyGroups(familyGroupIds, loadMode).flatMap(a => a?.data?.keys ?? [])
        return getLoadedInvites(keys)
    }

    async function getInvitesForFamilyGroupsAsync(familyGroupIds: (string | undefined)[], reload = false) {
        const loadMode = reload ? LoadMode.Reload : LoadMode.EnsureLoaded
        const keyListPromises = getAsyncKeyListsForFamilyGroups(familyGroupIds, loadMode).map(a => a.whenLoadCompleted)
        const keyLists = await Promise.all(keyListPromises)
        const keys = keyLists.filter(isDefined).flatMap(list => list.keys)
        return getLoadedInvites(keys)
    }

    function getAsyncKeyListForUser(userId?: string, loadMode = LoadMode.TrackOnly) {
        userId ??= tokenManager.current.userId
        const store = useFamilyGroupInviteStore() // use to get/return reactive store objects
        const loadInvitesAsync = async (sourceKeys: string[], requestTime: DateTime) => {
            const invitesPath = `users/${userId}/familygroupinvites`
            const plainItems = await gp.getPlainCollectionAsync(invitesPath, familyGroupInviteExpand + ",familyGroup/owner")
            console.log(`Loaded ${plainItems.length} family group invites at ${invitesPath}`)
            return processExpandedFamilyGroupInvites(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncKeyListForRelatedItems(userId, "User", userStore.asyncFamilyGroupInviteKeys,
            store.asyncInvites, "FamilyGroupInvite", loadMode, loadInvitesAsync)
    }

    function getInvitesForUser(userId?: string, loadMode = LoadMode.EnsureLoaded) {
        return getAsyncInviteList(getAsyncKeyListForUser(userId, loadMode)?.data?.keys ?? [])
            .map(a => a.data).filter(isDefined)
    }

    function getAsyncInviteByCode(id: string, code?: string) {
        const store = useFamilyGroupInviteStore() // use to get/return reactive store objects
        const loadAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const plainItem = await gp.getPlainAsync(`familygroupinvites/${id}`, { code, expand: familyGroupInviteExpand + ",familyGroup/owner" })
            return processExpandedFamilyGroupInvites([plainItem], requestTime)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncInvites, [id], "FamilyGroupInvite", LoadMode.EnsureLoaded, loadAsync)[0]
    }

    function getAsyncPermissions(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        const store = useFamilyGroupInviteStore() // use to get/return reactive store objects
        return getAsyncPermissionsCore(store.asyncPermissions, id, "FamilyGroupInvite", "familygroupinvites", loadMode)
    }
    
    async function sendAsync(invite: FamilyGroupInvite) {
        const store = useFamilyGroupInviteStore() // use to get/return reactive store objects
        const sendCoreAsync = async () => {
            const plainItem = await gp.postPlainAsync(`familygroupinvites`, invite)
            return plainToFamilyGroupInvite(plainItem)
        }
        // NOTE: Stored key list for family group will not be updated automatically
        const inviteId = await AsyncDataStore.addToStoreMapAsync(store.asyncInvites, "FamilyGroupInvite", inv => inv.id, sendCoreAsync)
        
        if (invite.familyGroupId) {
            familyGroupStore.asyncInviteKeys.delete(invite.familyGroupId)
        }
        return inviteId
    }

    async function cancelAsync(id: string) {
        await gp.postPlainAsync(`familygroupinvites/${id}/cancel`)
        getAsyncInvite(id, LoadMode.Reload) // trigger reload, but don't wait for it
    }

    async function resendAsync(id: string, code?: string, message?: string) {
        await gp.postPlainAsync(`familygroupinvites/${id}/resend`, { message }, { code })
        getAsyncInvite(id, LoadMode.Reload) // trigger reload, but don't wait for it
    }
    
    async function acceptAsync(id: string, code?: string) {
        const r = await gp.postPlainAsync(`familygroupinvites/${id}/accept`, undefined, { code }) as AcceptInviteResult
        getAsyncInvite(id, LoadMode.Reload) // trigger reload, but don't wait for it
        const memberStore = useFamilyGroupMemberStore()
        memberStore.getAsyncKeyListsForUsers([tokenManager.current.userId], LoadMode.Reload) // trigger reload, but don't wait for it
        return r.tokenResponse
    }
    
    const familyGroupInviteExpand = "senderUser,profileDataGroup/owner,recipientUser"

    function plainToFamilyGroupInvite(p: any) {
        const invite = assignExisting(new FamilyGroupInvite(), p)
        invite.result = p.result ?? InvitationResult.Pending // default
        invite.lastSentDate = DateTimeUtil.fromAPI(p.lastSentDate)
        invite.expirationDate = DateTimeUtil.fromAPI(p.expirationDate)
        invite.closedDate = DateTimeUtil.fromAPI(p.closedDate)
        invite.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)
        return invite
    }

    function processExpandedFamilyGroupInvites(plainInvites: any[], requestTime: DateTime) {
        userStore.addUsersToStore(plainInvites.map(p => p.senderUser).filter(isDefined), requestTime)
        userStore.addUsersToStore(plainInvites.map(p => p.recipientUser).filter(isDefined), requestTime)
        familyGroupStore.addFamilyGroupsToStore(plainInvites.map(p => p.familyGroup).filter(isDefined), requestTime)
        dataGroupStore.addDataGroupsToStore(plainInvites.map(p => p.profileDataGroup).filter(isDefined), requestTime)
        return AsyncDataStore.createItemMap(plainInvites, "FamilyGroupInvite", plainToFamilyGroupInvite, inv => inv.id!)
    }

    function getAsyncKeyListsForFamilyGroupInvites(sourceItemKeys: (string | undefined)[],
        sourceTypeName: string,
        relatedKeysStoreMap: Map<string, AsyncData<VersionedKeyList>>,
        loadMode: LoadMode,
        relatedItemsPath: (id: string) => string,
        relatedItemsExpand: string,
        getSourceKey: (fm: FamilyGroupInvite) => string) {

        const store = useFamilyGroupInviteStore() // use to get/return reactive store objects
        const loadAsync = async (sourceKeys: string[], requestTime: DateTime) => {
            const ids = sourceKeys.filter(isDefined)
            const path = relatedItemsPath(sourceKeys.length == 1 ? sourceKeys[0] : '$list')
            const query = { ids: sourceKeys.length == 1 ? undefined : ids.join() }
            const plainItems = await gp.getPlainCollectionAsync(path, relatedItemsExpand, query)
            console.log(`Loaded ${plainItems.length} family group invites at ${relatedItemsPath}`)
            return processExpandedFamilyGroupInvites(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncKeyListsForRelatedItems(sourceItemKeys, sourceTypeName, relatedKeysStoreMap,
            store.asyncInvites, "FamilyGroupInvite", loadMode, loadAsync, getSourceKey)
    }

    interface AcceptInviteResult {
        tokenResponse: AccessTokenResponse
    }
    return {
        asyncInvites,
        asyncPermissions,

        getAsyncInvite,
        getAsyncInviteList,
        getInviteListAsync,
        getAsyncKeyListsForFamilyGroups,
        getInvitesForFamilyGroups,
        getInvitesForFamilyGroupsAsync,
        getAsyncKeyListForUser,
        getInvitesForUser,
        getAsyncInviteByCode,
        getAsyncPermissions,
        sendAsync,
        cancelAsync,
        resendAsync,
        acceptAsync,
    }
})
